<template>
    <div>
        <DataTable
            v-model:filters="filters"
            v-model:first="first"
            columnResizeMode="fit"
            filterDisplay="menu"
            lazy
            :multiSortMeta="sort"
            paginator
            paginatorPosition="bottom"
            resizableColumns
            :pt="{
                pcPaginator: {
                    first: { 'data-test-id': 'search-paginator-first-button' },
                    prev: { 'data-test-id': 'search-paginator-prev-button' },
                    next: { 'data-test-id': 'search-paginator-next-button' },
                    last: { 'data-test-id': 'search-paginator-last-button' },
                },
            }"
            :rows="perPage"
            :rowsPerPageOptions="pageSizeOptions"
            scrollable
            scrollHeight="800px"
            showGridlines
            sortMode="multiple"
            tableStyle="table-layout: fixed; width: 100%;"
            :value="data"
            :totalRecords="totalItems"
            @filter="onFilter"
            @page="handlePageChange"
            @sort="handleSortChange"
        >
            <template #empty>
                <div
                    v-if="loadError"
                    class="search-error"
                    data-test-id="search-error-message"
                    role="alert"
                >
                    <div class="search-error__header">
                        <i class="pi pi-exclamation-triangle" />
                        <span>Error</span>
                    </div>
                    <ul class="search-error__list">
                        <li
                            v-for="(message, index) in loadError"
                            :key="index"
                            :data-test-id="`search-error-message-line-${index}`"
                        >
                            {{ message }}
                        </li>
                    </ul>
                </div>
                <span
                    v-else
                    data-test-id="search-no-results-text"
                    >{{ noResultsMessage }}</span
                >
            </template>
            <template #header>
                <div class="search-header">
                    <IconField class="search-header__search">
                        <InputIcon
                            class="pi pi-search"
                            :pt="makeTestIdPassThrough('full-text-search-icon')"
                        />
                        <InputText
                            v-model="fullTextSearch"
                            class="p-column-filter"
                            placeholder="Keyword Search"
                            :pt="makeTestIdPassThrough('full-text-search-text-field')"
                            type="text"
                            style="width: 100%"
                            @input="onFullTextInput"
                            @keydown.enter="handleFullTextChange"
                        />
                        <InputIcon
                            v-show="hasFullTextSearch"
                            class="pi pi-times search-header__clear"
                            :pt="makeTestIdPassThrough('full-text-search-clear-button')"
                            @click="clearFullTextSearch"
                        />
                    </IconField>
                    <PrimeButton
                        aria-label="Clear all filters and sorting"
                        data-test-id="search-clear-all-button"
                        icon="pi pi-filter-slash"
                        label="Clear All"
                        outlined
                        severity="secondary"
                        title="Clear all filters and sorting"
                        @click="reset"
                    />
                    <PrimeButton
                        aria-label="Reload search"
                        data-test-id="search-reload-button"
                        icon="pi pi-refresh"
                        outlined
                        severity="secondary"
                        title="Reload search"
                        @click="reload"
                    />
                </div>
            </template>
            <Column
                v-for="column in normalizedColumns"
                :key="column.field"
                :field="column.field"
                :header="columnHeader(column)"
                :sortable="column.sortable"
                :showFilterMatchModes="false"
                :showFilterMenu="column.filterable"
                :bodyStyle="column.dataType === 'actions' ? { padding: 0, position: 'relative' } : null"
                :style="columnStyles[column.field]"
                :pt="{
                    columnTitle: { 'data-test-id': `search-column-header-label-${column.field}` },
                    sortIcon: { 'data-test-id': `search-column-header-sort-button-${column.field}` },
                    pcColumnFilterButton: { 'data-test-id': `search-column-filter-menu-button-${column.field}` },
                    pcFilterClearButton: {
                        root: { 'data-test-id': `search-column-filter-clear-button-${column.field}` },
                    },
                }"
            >
                <template #filter="{ filterModel, applyFilter }">
                    <EnumFilter
                        v-if="column.dataType === 'enum'"
                        v-model="filterModel.value"
                        :field="column.field"
                        :options="column.options"
                    />
                    <DateFilter
                        v-else-if="isDateColumn(column)"
                        v-model="filterModel.value"
                        :field="column.field"
                        :label="column.label"
                        @update:valid="setFilterValidity(column.field, $event)"
                    />
                    <InputText
                        v-else
                        v-model="filterModel.value"
                        :data-test-id="`${column.field}-filter-field`"
                        type="text"
                        :placeholder="`Filter by ${column.label}`"
                        @keydown.enter.stop.prevent="applyFilterAndClose(applyFilter)"
                    />
                </template>
                <template #filterapply="{ filterCallback }">
                    <PrimeButton
                        class="p-datatable-filter-apply-button"
                        :data-test-id="`search-column-filter-apply-button-${column.field}`"
                        :disabled="!isFilterValid(column.field)"
                        label="Apply"
                        size="small"
                        type="button"
                        @click="filterCallback()"
                    />
                </template>
                <template #body="{ data: row, field }">
                    <Skeleton v-if="loading" />
                    <TextCell
                        v-else-if="column.dataType === 'string'"
                        :data-test-id="makeCellTestId(row, column)"
                        :value="row[field]"
                    />
                    <EnumCell
                        v-else-if="column.dataType === 'enum'"
                        :data-test-id="makeCellTestId(row, column)"
                        :options="column.options"
                        :value="row[field]"
                    />
                    <DateCell
                        v-else-if="column.dataType === 'date' || column.dataType === 'datetime'"
                        :data-test-id="makeCellTestId(row, column)"
                        :show-time="column.dataType === 'datetime'"
                        :value="row[field]"
                    />
                    <LinkCell
                        v-else-if="column.dataType === 'link'"
                        :data-test-id="makeCellTestId(row, column)"
                        :value="row[field]"
                    />
                    <ActionsCell
                        v-else-if="column.dataType === 'actions'"
                        :actions="row[field]"
                        :row-urn="row.urn"
                        :field="field"
                        @action-performed="onActionPerformed"
                    />
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script>
import { debounce, isString } from 'lodash'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import PrimeButton from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import InputText from 'primevue/inputtext'
import InputIcon from 'primevue/inputicon'
import IconField from 'primevue/iconfield'
import 'primeicons/primeicons.css'
import { FilterMatchMode } from '@primevue/core/api'

import ActionsCell from './ActionsCell.vue'
import DateCell from './DateCell.vue'
import DateFilter from './DateFilter.vue'
import EnumCell from './EnumCell.vue'
import EnumFilter from './EnumFilter.vue'
import LinkCell from './LinkCell.vue'
import TextCell from './TextCell.vue'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import SEARCH_DEBOUNCE_MS from '@/constants/search-debounce'

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]
const PAGE_SIZE = PAGE_SIZE_OPTIONS[0]
// Data types filtered by a date comparison rather than by text.
const DATE_DATA_TYPES = ['date', 'datetime']
// The date operator whose value is a pair of dates rather than one.
const BETWEEN_OPERATOR = 'between'
// 'link' is included so a link column (e.g. the assembly relay) can
// contains-filter and sort on the text the backend indexes for it.
const FILTERABLE_DATA_TYPES = ['string', 'enum', 'link', ...DATE_DATA_TYPES]
// Actions is just an icon button, so it defaults to a narrow fixed width
// instead of taking a proportional share.
const ACTIONS_COLUMN_WIDTH = '50px'
// Floor so a column stays usable when the leftover space is divided thin;
// the table scrolls horizontally if the floors add up past the container.
const MIN_COLUMN_WIDTH = '75px'

export default {
    name: 'SearchIndex',

    components: {
        ActionsCell,
        Column,
        DataTable,
        DateCell,
        DateFilter,
        EnumCell,
        EnumFilter,
        LinkCell,
        IconField,
        InputIcon,
        InputText,
        PrimeButton,
        Skeleton,
        TextCell,
    },

    mixins: [needsOrganizationSlug],

    props: {
        columns: {
            type: Array,
            required: true,
            validator(value) {
                return value.every((column) => {
                    const { customCell, dataType, filterable, label, labelSuffix, options, sortable, width } = column
                    // Every column needs a header label except actions
                    const requiredProps = dataType === 'actions' ? [dataType] : [label, dataType]
                    const stringProps = [label, labelSuffix, dataType, customCell, width]
                    const booleanProps = [sortable, filterable]
                    const arrayProps = [options]

                    const dataTypes = ['string', 'date', 'datetime', 'enum', 'custom', 'actions', 'link']

                    if (dataType === 'custom') {
                        requiredProps.push(customCell)
                    } else if (dataType === 'enum') {
                        requiredProps.push(options)
                    }

                    const hasRequired = requiredProps.every((val) => val !== undefined && val !== null)

                    if (!hasRequired) {
                        return false
                    }

                    const hasValidStrings = stringProps.every(
                        (val) => isString(val) || val === null || val === undefined
                    )

                    if (!hasValidStrings) {
                        return false
                    }

                    const hasValidBooleans = booleanProps.every(
                        (val) => val === null || val === undefined || val === true || val === false
                    )

                    if (!hasValidBooleans) {
                        return false
                    }

                    const hasValidArrays = arrayProps.every(
                        (val) => val === null || val === undefined || Array.isArray(val)
                    )

                    if (!hasValidArrays) {
                        return false
                    }

                    return dataTypes.includes(dataType)
                })
            },
        },
        noResultsMessage: {
            type: String,
            required: false,
            default: 'No results found.',
        },
        view: {
            type: String,
            required: false,
            default: null,
        },
    },

    emits: ['page-loaded'],

    data() {
        return {
            currentPage: 0,
            data: Array.from({ length: PAGE_SIZE }),
            filters: {},
            // Filter fields a filter component has reported as not applyable
            // yet, keyed by field. A filter with no entry is applyable, so a
            // filter component only has to say anything if it has a state
            // that cannot be searched on.
            invalidFilters: {},
            first: 0,
            lastLoadedIndex: null,
            loading: true,
            loadError: null,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            perPage: PAGE_SIZE,
            sort: [],
            totalItems: PAGE_SIZE,
            fullTextSearch: null,
        }
    },

    computed: {
        hasFullTextSearch() {
            return isString(this.fullTextSearch) && this.fullTextSearch.length > 0
        },

        normalizedColumns() {
            return this.columns.map((column) => ({
                ...column,
                filterable: column.filterable === true && FILTERABLE_DATA_TYPES.includes(column.dataType),
            }))
        },

        // A per-column width style, keyed by field. A column keeps an
        // explicit `width` if it defines one; the actions column defaults
        // to a fixed narrow width; every other column splits the leftover
        // space equally, so widths stay proportional on load without each
        // view having to specify them. Under fixed layout these widths
        // then bound the cell content and grow/shrink as the user resizes.
        columnStyles() {
            const fixedWidths = this.normalizedColumns.map((column) => this.explicitColumnWidth(column))
            const flexibleCount = fixedWidths.filter((width) => width === null).length
            const flexibleWidth = this.flexibleColumnWidth(fixedWidths.filter(Boolean), flexibleCount)

            return this.normalizedColumns.reduce((styles, column, index) => {
                const width = fixedWidths[index] ?? flexibleWidth
                const minWidth = column.dataType === 'actions' ? ACTIONS_COLUMN_WIDTH : MIN_COLUMN_WIDTH
                styles[column.field] = `width: ${width}; min-width: ${minWidth}`
                return styles
            }, {})
        },
    },

    watch: {
        async view() {
            await this.reset()
        },

        // Keeps the filter model in sync when the column set changes on its own.
        // A filterable column with no entry here crashes PrimeVue's filter menu.
        normalizedColumns() {
            this.resetFilters()
        },
    },

    created() {
        this.filters = this.buildDataTableFilters()
        this.debouncedFullTextChange = debounce(this.handleFullTextChange, SEARCH_DEBOUNCE_MS)
        // The AbortController of the in-flight load, so a newer load can cancel
        // it. Kept off the reactive data so its signal is a real AbortSignal.
        this.pendingRequest = null
    },

    async mounted() {
        await this.loadData()
    },

    beforeUnmount() {
        this.cancelPendingFullTextChange()
        this.pendingRequest?.abort()
    },

    methods: {
        // A filter component owns the rule for what it can be applied with
        // and reports the verdict through `update:valid`; all this knows is
        // that an invalid one cannot be applied. Anything rendered in the
        // filter slot can opt in by emitting the same event.
        setFilterValidity(field, valid) {
            if (valid) {
                delete this.invalidFilters[field]

                return
            }

            this.invalidFilters[field] = true
        },
        isFilterValid(field) {
            return this.invalidFilters[field] !== true
        },
        async applyFilterAndClose(applyFilter) {
            applyFilter()
            await this.$nextTick()
            window.document?.activeElement?.blur()
        },
        resetFilters() {
            this.filters = this.buildDataTableFilters()
            this.invalidFilters = {}
        },
        buildDataTableFilters() {
            const filters = {}

            this.normalizedColumns
                .filter((column) => column.filterable === true)
                .forEach((column) => {
                    filters[column.field] = {
                        value: null,
                        matchMode: this.filterMatchMode(column),
                    }
                })

            return filters
        },
        // Filtering happens server side, so the match mode only has to be
        // one PrimeVue accepts for the column's type; the operator that
        // reaches the backend comes from the filter's own model.
        filterMatchMode(column) {
            if (column.dataType === 'enum') {
                return FilterMatchMode.IN
            }

            return this.isDateColumn(column) ? FilterMatchMode.DATE_IS : FilterMatchMode.CONTAINS
        },
        isDateColumn(column) {
            return DATE_DATA_TYPES.includes(column.dataType)
        },
        async onFilter() {
            await this.handleFilterChange()
        },
        async reset() {
            this.cancelPendingFullTextChange()
            this.resetFilters()
            this.fullTextSearch = null
            this.sort = []
            this.currentPage = 0
            this.first = 0
            await this.loadData()
        },
        async reload() {
            this.cancelPendingFullTextChange()
            await this.loadData()
        },
        async onActionPerformed(action) {
            if (action?.refreshes_results === false) {
                return
            }

            await this.reload()
        },
        async clearFullTextSearch() {
            this.cancelPendingFullTextChange()
            this.fullTextSearch = null
            await this.handleFilterChange()
        },
        cancelPendingFullTextChange() {
            if (this.debouncedFullTextChange) {
                this.debouncedFullTextChange.cancel()
            }
        },
        onFullTextInput() {
            this.debouncedFullTextChange()
        },
        async handleFullTextChange() {
            this.cancelPendingFullTextChange()

            if (this.hasFullTextSearch && this.fullTextSearch.trim().length === 0) {
                this.fullTextSearch = null
            }

            await this.handleFilterChange()
        },
        async handleFilterChange() {
            this.currentPage = 0
            this.first = 0
            await this.loadData()
        },
        async handlePageChange(event) {
            this.perPage = event.rows
            this.currentPage = event.page
            this.first = event.first
            await this.loadData()
        },
        async handleSortChange(event) {
            this.sort = event.multiSortMeta
            this.currentPage = 0
            this.first = 0
            await this.loadData()
        },
        buildRequestFilters() {
            return this.normalizedColumns
                .filter((column) => column.filterable === true)
                .flatMap((column) => {
                    // An enum column always sends its filter, even
                    // untouched, so that its options act as an allow list.
                    if (column.dataType === 'enum') {
                        return this.buildEnumFilter(column)
                    }

                    // A date column sends the one filter its mode names,
                    // or none at all while it is untouched.
                    if (this.isDateColumn(column)) {
                        return this.buildDateFilter(column)
                    }

                    return this.hasTextFilterValue(column.field) ? this.buildStringFilter(column.field) : []
                })
        },
        hasTextFilterValue(field) {
            const filter = this.filters[field]

            return !!filter && filter.value !== null && filter.value !== ''
        },
        buildStringFilter(field) {
            return {
                field,
                operator: 'contains',
                value: this.filters[field].value,
            }
        },
        // A date filter posts its mode as the operator, so the request is
        // an empty list or a single filter.
        buildDateFilter(column) {
            const filter = this.filters[column.field]?.value
            const value = this.filterDateValue(filter)

            if (value === null) {
                return []
            }

            return [{ field: column.field, operator: filter.mode, value }]
        },
        // The dates a date filter is ready to post, or null when it has
        // nothing to send. A BETWEEN range waits on both of its ends, since
        // that operator compares against a start and an end.
        //
        // Dates are sent with the browser's offset so that the day the user
        // picked is the day the backend bounds, rather than the UTC day it
        // happens to land in.
        filterDateValue(filter) {
            if (!filter?.mode || !filter.value) {
                return null
            }

            if (filter.mode !== BETWEEN_OPERATOR) {
                return window.moment(filter.value).format()
            }

            const range = filter.value.filter(Boolean)

            return range.length === 2 ? range.map((date) => window.moment(date).format()) : null
        },
        buildEnumFilter(column) {
            // A null value means untouched, which defaults to every
            // option. An empty array means the user deselected them all,
            // and is sent as is so that nothing matches.
            const selected = this.filters[column.field]?.value ?? column.options.map(({ value }) => value)

            return {
                field: column.field,
                operator: 'in',
                value: selected,
            }
        },
        async loadData() {
            // Supersede any in-flight load so a slow earlier one can neither
            // land after this one nor report its own (now stale) failure.
            this.pendingRequest?.abort()
            const controller = new AbortController()
            this.pendingRequest = controller

            this.loading = true
            this.loadError = null
            const url = `${this.organizationSlug}/search`

            const request = {
                filters: this.buildRequestFilters(),
                sort: this.sort.map((sortItem) => ({
                    property: sortItem.field,
                    direction: sortItem.order === 1 ? 'asc' : 'desc',
                })),
            }

            try {
                const response = await this.$http.post(
                    url,
                    {
                        count: this.perPage,
                        filters: request.filters,
                        page: this.currentPage + 1,
                        sort: request.sort,
                        text: this.fullTextSearch,
                        view: this.view,
                    },
                    { signal: controller.signal }
                )

                // A newer load superseded this one while it was in flight
                if (this.pendingRequest !== controller) {
                    return
                }

                const { total, data } = response.data
                this.totalItems = total
                this.data = data

                this.$emit('page-loaded', data.length, total)
            } catch (error) {
                // A load we superseded (or aborted on unmount)
                if (this.isAbortError(error) || this.pendingRequest !== controller) {
                    return
                }

                // Clear the rows so the banner is not read as a caption on
                // stale results, and report zero so any results count reflects
                // what is shown rather than lingering on the last load.
                this.loadError = this.describeLoadError(error, request)
                this.data = []
                this.totalItems = 0
                this.$emit('page-loaded', 0, 0)
            } finally {
                // Only the newest load owns the loading flag; a superseded one
                // must not clear it out from under its replacement.
                if (this.pendingRequest === controller) {
                    this.loading = false
                    this.pendingRequest = null
                }
            }
        },
        isAbortError(error) {
            return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError'
        },
        describeLoadError(error, request) {
            const response = error?.response

            if (!response) {
                return ['Search is temporarily unavailable. Please try again.']
            }

            if (response.status === 422) {
                return this.describeValidationError(response.data, request)
            }

            if (response.status === 503) {
                return ['Search is temporarily unavailable. Please try again shortly.']
            }

            return ['Something went wrong running your search. Please try again.']
        },
        describeValidationError(data, request) {
            const searchErrors = data?.search_errors

            if (Array.isArray(searchErrors) && searchErrors.length > 0) {
                const messages = searchErrors.map((searchError) => this.formatSearchError(searchError, request))

                // remove duplicates
                return [...new Set(messages)]
            }

            // A 422 without the structured list still gets a message rather
            // than a silent failure.
            return ['Your search could not be run. Please adjust it and try again.']
        },
        // A backend error is a request path plus a message. We resolve the
        // path against the request that failed to see if a label is available
        // for the path, then read it as one sentence, e.g. filters.0.value +
        // "must be a valid date." becomes "Created must be a valid date."
        formatSearchError({ path, message }, request) {
            const subject = this.errorSubject(path, request)

            return subject ? `${subject} ${message}` : message
        },
        // The subject a request path is about. A filters.N / sort.N entry is
        // resolved through the request that failed to the field it targeted (and
        // from there to its column label); a top-level path (text, filters, …)
        // is that property itself.
        errorSubject(path, request) {
            const [group, index] = String(path).split('.')

            if (group === 'filters' && index !== undefined) {
                return this.columnSubject(request?.filters?.[Number(index)]?.field)
            }

            if (group === 'sort' && index !== undefined) {
                return this.columnSubject(request?.sort?.[Number(index)]?.property)
            }

            return group
        },
        // A field's column label, or the raw field when it is not one of our
        // columns (e.g. the field itself was the invalid part). Null when there
        // is no field to name at all.
        columnSubject(field) {
            if (!field) {
                return null
            }

            return this.columnLabel(field) ?? field
        },
        // The plain header label for a field, without its display suffix, so
        // it reads cleanly mid sentence in an error message. Null when the
        // field is not one of our columns.
        columnLabel(field) {
            const column = this.columns.find((candidate) => candidate.field === field)

            return column?.label ?? null
        },
        // The column's header as shown in the table: its label followed by an
        // optional suffix (e.g. a timezone "(EDT)"). The suffix is display only
        // and is left off everywhere the plain label is used.
        columnHeader(column) {
            return column.labelSuffix ? `${column.label} ${column.labelSuffix}` : column.label
        },
        // The column's own width, or null when it should flex. An explicit
        // width wins; the actions column falls back to its fixed width.
        explicitColumnWidth(column) {
            if (column.width) {
                return column.width
            }

            if (column.dataType === 'actions') {
                return ACTIONS_COLUMN_WIDTH
            }

            return null
        },
        // The share each flexible column gets of the space left once the
        // fixed columns are accounted for. calc() lets fixed px and %
        // widths mix in the subtraction.
        flexibleColumnWidth(fixedWidths, flexibleCount) {
            if (flexibleCount === 0) {
                return null
            }

            if (fixedWidths.length === 0) {
                return `calc(100% / ${flexibleCount})`
            }

            return `calc((100% - (${fixedWidths.join(' + ')})) / ${flexibleCount})`
        },
        makeCellTestId(row, column) {
            return `row-${row.urn}-column-${column.field}-cell`
        },
        makeTestIdPassThrough(dataTestId) {
            return {
                root: {
                    'data-test-id': dataTestId,
                },
            }
        },
    },
}
</script>

<style scoped>
.search-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
}

.search-header__search {
    flex: 1 1 auto;
    min-width: 0;
}

.search-header__clear {
    cursor: pointer;
}

.search-error {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #f87171;
    border-radius: 4px;
    background-color: #fef2f2;
    color: #b91c1c;
    text-align: left;
}

.search-error__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
}

.search-error__list {
    margin: 0;
    padding-left: 1.5rem;
    list-style: disc;
}
</style>
